import * as React from "react"
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil"
import { Box, Flex } from "@chakra-ui/react"
import { localUserIdAtom, usersSelector } from "../../../../recoil/atoms/users"

import { useLocalUserType } from "../../../../hooks/useLocalUserType"
import useWindowSize from "../../../../hooks/useWindowSize"
import { useBrowserType } from "../../../../hooks/useBrowserType"
import layoutStates from "../../../../recoil/atoms/layout"
import {
  brandingSelector,
  roomSelector,
  showTabsAtBottomOnMobileSelector
} from "../../../../recoil/atoms/room"
import { useResizeObserver } from "../../../../hooks/useResizeObserver"
import { MobileHeader } from "../mobile-header"
import { MobileMainMediaScreen } from "../mobile-main-media-screen"
import { AuctionOverlayBid } from "../../../servicesPanel/auctions/auction-overlay-bid"
import { AuctionFinalOverlay } from "../../../servicesPanel/auctions/auction-final-overlay"
import {
  auctionParamsSelector,
  auctionFinalResultAtom
} from "../../../../recoil/atoms/auction"
import { usePrevSpeakerStreamsResizeObserver } from "../../../../hooks/useResizeObserver/usePrevSpeakerStreamsResizeObserver"
import { findSideRatio } from "../../../../util/findSideRatio"
import { MobilePrevSpeakerStreams } from "../mobile-prev-speaker-steams"
import { usersListSmallWindowSelector } from "../../../../recoil/atoms/userListSmallWindow"
import { useWatchParty } from "../../../../hooks/useWatchParty"
import {
  CHAT_INPUT_FIELD_HEIGHT,
  FOOTER_HEIGHT,
  HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB,
  SELECTED_REPLY_MESSAGE_HEIGHT
} from "../../../../constants"
import { MobileServicePanel } from "../mobile-service-panel"

import { StyledResizableBox } from "../../../common/resizableBox"
import { productOverlaySelector } from "../../../../recoil/atoms/product-overlay"
import { pipParamsSelector } from "../../../../recoil/atoms/pictureInPicture"
import { initialVideoResolutionOnMobileValues } from "../../../../constants/enums"
import { viewDependingLayoutAtom } from "../../../../recoil/atoms/view"
import useLocalUserHasPermissions from "../../../../hooks/useLocalUserHasPermissions"
import { shoppableVideosSelector } from "../../../../recoil/selectors/shoppable-videos-selector"
import { ConsoleLogger } from "../../../../util/logger"
import { mainWindowResolutionAtom } from "../../../../recoil/atoms/mainWindowResolution"
import { selectedReplyAtom } from "../../../../recoil/atoms/chats"
import styled from "@emotion/styled"
import { AvatarVideoSize } from "../../../../recoil/atoms/avatars/types"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"
import { windowDimensions } from "src/util/windowWrapper"
import { nativeMobileSafeAreaAtom } from "src/recoil/atoms/nativeMobileSafeArea"

const logger = new ConsoleLogger()

interface MobileViewProps {}

const ShadowBar = styled(Box)`
  -webkit-align-content: end;
  align-content: end;
  background: black;
  height: 30px;
  --scrollbar-width-product-description: 1px;
  --mask-height-product-description: 30px;
  overflow-y: auto;
  --mask-size-content-product-description: calc(
      100% - var(--scrollbar-width-product-description)
    )
    100%;
  --mask-image-scrollbar-product-description: linear-gradient(white, white);
  --mask-size-scrollbar-product-description: var(
      --scrollbar-width-product-description
    )
    100%;
  mask-image: ${() =>
    `linear-gradient(
    to top,
    transparent,
    black var(--mask-height-product-description),
    black calc(100% - 0px),
    transparent
  ),
  var(--mask-image-scrollbar-product-description)`};
  mask-size: var(--mask-size-content-product-description),
    var(--mask-size-scrollbar-product-description);
  mask-position: 0 0, 100% 0;
  mask-repeat: no-repeat, no-repeat;
`

const _MobileView: React.FC<MobileViewProps> = (): JSX.Element => {

  const selectedAvatarConfig = useCurrentAvatarInfo()
  const MAIN_WINDOW_CONTAINER = "main-window-container"
  const setViewDependingLayout = useSetRecoilState(viewDependingLayoutAtom)
  const showTabsAtBottomOnMobile = useRecoilValue(
    showTabsAtBottomOnMobileSelector
  )

  React.useEffect(() => {
    setViewDependingLayout(prevState => ({ ...prevState, isMobile: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const mobileMeetingContainerRef = React.useRef<HTMLDivElement>(null)
  const smallScreensContainerRef = React.useRef<HTMLDivElement>(null)

  const { isOpen } = useRecoilValue(productOverlaySelector)
  const [, height] = useResizeObserver(mobileMeetingContainerRef)
  const { heightPrevSpeaker } = usePrevSpeakerStreamsResizeObserver(
    smallScreensContainerRef
  )
  const { isActivePiP } = useRecoilValue(pipParamsSelector)
  const [fullScreen, setFullScreen] = useRecoilState(layoutStates.fullScreen)
  const [isWasFullScreen, setIsWasFullScreen] = React.useState(false)
  const room = useRecoilValue(roomSelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const _localUserId = useRecoilValue(localUserIdAtom)
  const localUser = useRecoilValue(usersSelector(_localUserId))

  const { isModalBid } = useRecoilValue(auctionParamsSelector)
  const { isFinalModal } = useRecoilValue(auctionFinalResultAtom)

  const [targetTabIndex, setTargetTabIndex] = useRecoilState<number>(
    layoutStates.historyTabEnabledMobile
  )
  const userIdsSmallWindows = useRecoilValue(usersListSmallWindowSelector)

  const { isSafari, isChromeIOS, isTabletView, isNativeAppBrowser } =
    useBrowserType()
  const { screenSize, setScreenSize } = useWindowSize()
  const { isModerator } = useLocalUserType()
  const { isWatchParty, watchPartyHostId, wpSrc } = useWatchParty()
  const { canSeeHistory, canUseChat } = useLocalUserHasPermissions()

  const [videoSize, setVideoSize] = useRecoilState(mainWindowResolutionAtom)

  const TRANSPARENT_START_HEIGHT = 5

  // State to store boolean value, True - if video wrap whole screen else False
  const [isVideoWrapWholeScreen, setIsVideoWrapWholeScreen] =
    React.useState<boolean>(null)
  const isVideoWrappedWholeScreenRef = React.useRef(isVideoWrapWholeScreen)

  const [videoConstraints, setVideoConstraints] = React.useState(
    Object.values([screenSize.width, screenSize.height - 75])
  )

  const isLandscape = window.matchMedia("(orientation: landscape)").matches

  const localUserProperties = React.useMemo(() => {
    return {
      localUserId: localUser?.id,
      type: localUser?.type,
      camEnabled: localUser?.camEnabled,
      micEnabled: localUser?.micEnabled,
      permissions: localUser?.permissions,
      oldPermissions: localUser?.oldPermissions,
      mediaConnected: localUser?.mediaConnected,
      screenShareEnabled: localUser?.screenShareEnabled,
      color: localUser?.color,
      handUp: localUser?.handUp
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localUser?.id,
    localUser?.type,
    localUser?.camEnabled,
    localUser?.micEnabled,
    localUser?.permissions,
    localUser?.oldPermissions,
    localUser?.mediaConnected,
    localUser?.screenShareEnabled,
    localUser?.handUp
  ])

  const isShoppableVideos = useRecoilValue(shoppableVideosSelector)

  const isOnlyScreensView = React.useMemo<boolean>(
    () =>
      !canUseChat &&
      !canSeeHistory &&
      !isModerator &&
      !isLandscape &&
      !fullScreen &&
      !isShoppableVideos,
    [
      canUseChat,
      canSeeHistory,
      isModerator,
      isLandscape,
      fullScreen,
      isShoppableVideos
    ]
  )

  const isServicePanel = React.useMemo<boolean>(() => {
    return (
      !fullScreen &&
      // !isLandscape &&
      (canUseChat || canSeeHistory || isShoppableVideos)
    )
  }, [canSeeHistory, canUseChat, fullScreen, isLandscape, isShoppableVideos])

  const [chatSize, setChatSize] = React.useState({ width: null, height: null })

  /** Set Main Window Draggable */
  const [isDraggable, setIsDraggable] = React.useState(true);
  const [isKeyboardActive, setIsKeyboardActive] = React.useState(false);
  const setEnableSafeArea = useSetRecoilState(nativeMobileSafeAreaAtom)

  const heightMobileMeeting = React.useMemo(
    () => (isChromeIOS ? height : screenSize.height),
    [height, isChromeIOS, screenSize.height]
  )

  const [isFirstRender, setFirstRender] = useRecoilState(
    layoutStates.firstRenderMobile
  )

  // const calculateCurrentHeight = (prevScreenHeight, curScreenHeight, prevMainHeight) => {
  //   return (prevMainHeight * curScreenHeight) / prevScreenHeight
  // }

  const isTransparent = React.useMemo<boolean>(() => {
    return (
      isVideoWrapWholeScreen ||
      // (screenSize.height &&
      //   screenSize.height -
      //     (showTabsAtBottomOnMobile
      //       ? HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB + TRANSPARENT_START_HEIGHT
      //       : targetTabIndex === 2
      //       ? 5
      //       : 45) <
      //     videoSize.height) ||
      fullScreen ||
      isLandscape
    )
  }, [
    fullScreen,
    // screenSize.height,
    // targetTabIndex,
    // videoSize.height,
    isLandscape,
    isVideoWrapWholeScreen
  ])

  const defaultHeightMainScreen = React.useMemo<number>(() => {
 
    const isDefaultVideoSizeSquare = selectedAvatarConfig?.videoSize
      ? selectedAvatarConfig?.videoSize === AvatarVideoSize.square
      : room?.initialVideoResolutionMobilePortrait ===
        initialVideoResolutionOnMobileValues.SQUARE

    const isDefaultVideoSizeFullScreen = selectedAvatarConfig?.avatarId
      ? selectedAvatarConfig?.videoSize === AvatarVideoSize.fullscreen
      : room?.initialVideoResolutionMobilePortrait ===
        initialVideoResolutionOnMobileValues.FULLSCREEN

    const height = !isFirstRender
      ? isDefaultVideoSizeSquare
        ? findSideRatio(screenSize.width, 1, 1)
        : isDefaultVideoSizeFullScreen
        ? // minus the bottom tab size if bottom tab is shown
          heightMobileMeeting -
          (showTabsAtBottomOnMobile
            ? HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB
            : 0)
        : findSideRatio(screenSize.width, 9, 16)
      : isTransparent
      ? heightMobileMeeting -
        (showTabsAtBottomOnMobile ? HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB : 0)
      : findSideRatio(screenSize.width, 9, 16)

    return height
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    heightMobileMeeting,
    room?.initialVideoResolutionMobilePortrait,
    selectedAvatarConfig?.videoSize,
    userIdsSmallWindows.length,
    screenSize.width,
    isActivePiP,
    isOpen,
    showTabsAtBottomOnMobile
  ])

  React.useEffect(() => {
    setViewDependingLayout(prevState => ({
      ...prevState,
      isTransparent: isTransparent,
      isLandscape: isLandscape
    }))
  }, [isLandscape, isTransparent, setViewDependingLayout])

  React.useEffect(() => {
    let timer = setTimeout(() => setFirstRender(true), 2000)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const videoSizeDefault = React.useMemo(() => {
    return { width: screenSize.width, height: defaultHeightMainScreen }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultHeightMainScreen, screenSize, isFirstRender])

  const marginBottomMainScreen = React.useMemo<number>(() => {
    if (
      watchPartyHostId === "modNotPresentVideo" ||
      (wpSrc && wpSrc.endsWith(".mp3"))
    ) {
      return 0
    }
    if (userIdsSmallWindows.length === 0) {
      return 0
    }
    if (userIdsSmallWindows.length > 0) {
      return 20
    }
    return 0
  }, [userIdsSmallWindows.length, watchPartyHostId, wpSrc])

  const chatSizeDefault = React.useMemo(() => {
    return {
      width: screenSize.width,
      height:
        heightMobileMeeting -
        videoSizeDefault.height -
        heightPrevSpeaker -
        marginBottomMainScreen
    }
  }, [
    heightMobileMeeting,
    heightPrevSpeaker,
    marginBottomMainScreen,
    screenSize.width,
    videoSizeDefault.height
  ])

  const changeToCurrentLayout = React.useCallback(() => {
    setVideoSize(videoSizeDefault)
    setChatSize(chatSizeDefault)
    // logger.log(`Video Size:: ${JSON.stringify(videoSizeDefault)}.`)

    // minus bottom tab-bar height
    setVideoConstraints([
      screenSize.width,
      screenSize.height -
        (showTabsAtBottomOnMobile ? HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB : 0)
    ])

  }, [
    chatSizeDefault,
    screenSize.height,
    screenSize.width,
    videoSizeDefault,
    showTabsAtBottomOnMobile
  ])

  React.useEffect(() => {
    if (!isFirstRender) {
      changeToCurrentLayout()
    }
  }, [isActivePiP, isFirstRender, changeToCurrentLayout])

  React.useEffect(() => {
    if (isFirstRender) {
      changeToCurrentLayout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    isLandscape,
    isActivePiP,
    screenSize.height,
    chatSizeDefault,
    isOnlyScreensView
  ])

  React.useEffect(() => {

    const isDefaultVideoSizeFullScreen = selectedAvatarConfig?.avatarId
      ? selectedAvatarConfig?.videoSize === AvatarVideoSize.fullscreen
      : room?.initialVideoResolutionMobilePortrait ===
        initialVideoResolutionOnMobileValues.FULLSCREEN

    // check if video resolution is set to whole-screen
    if (isDefaultVideoSizeFullScreen) {
      setIsVideoWrapWholeScreen(true)
      // Deselect the tab when the video is in full-screen mode.
      setTargetTabIndex(-1)
    }

    if (isFirstRender && !isTransparent) {
      changeToCurrentLayout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // check is video wrapped full screen and update the state accordingly
  const checkIsVideoWrappedWholeScreen = React.useCallback(
    async (height: number) => {
      const wholeScreen =
        screenSize.height &&
        screenSize.height -
          (showTabsAtBottomOnMobile
            ? HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB + TRANSPARENT_START_HEIGHT
            : targetTabIndex === 2
            ? 5
            : 45) <
          height

      if (isVideoWrappedWholeScreenRef.current !== wholeScreen) {
        isVideoWrappedWholeScreenRef.current = wholeScreen
        setIsVideoWrapWholeScreen(wholeScreen)
        // Deselect the tab when the video is in full-screen mode.
        setTargetTabIndex(-1)
      }
    },
    [showTabsAtBottomOnMobile, screenSize.height]
  )

  const onResizeVideo = React.useCallback(
    (event, { size }) => {
      setVideoSize({ width: size.width, height: size.height })

      setChatSize({
        width: screenSize.width,
        height:
          heightMobileMeeting -
          size.height -
          heightPrevSpeaker -
          (userIdsSmallWindows.length > 0 ? 20 : 0)
      })

      checkIsVideoWrappedWholeScreen(size.height)

      event.stopPropagation()
    },
    [
      heightMobileMeeting,
      heightPrevSpeaker,
      screenSize.width,
      userIdsSmallWindows.length,
      checkIsVideoWrappedWholeScreen
    ]
  )

  const isPrevSpeakerStreams = React.useMemo<boolean>(() => {
    return userIdsSmallWindows.length > 0
  }, [userIdsSmallWindows.length])

  const heightSmallScreensContainer = React.useMemo<string>(() => {
    if (isWatchParty && userIdsSmallWindows.length === 0) {
      return "0px"
    }
    if (userIdsSmallWindows.length === 1) {
      return isWatchParty ? "18vw" : "20vw"
    }
    if (userIdsSmallWindows.length === 2) {
      return isWatchParty ? "16vw" : "18vw"
    }
    if (userIdsSmallWindows.length === 3) {
      return isWatchParty ? "12vw" : "16vw"
    }
    if (userIdsSmallWindows.length === 4) {
      return isWatchParty ? "10vw" : "12vw"
    }
    return "0px"
  }, [isWatchParty, userIdsSmallWindows.length])

  const smallVideosPosition = React.useMemo(() => {
    return videoSize.height < videoSizeDefault.height
      ? videoSize.height
      : videoSize.height
  }, [videoSize.height, videoSizeDefault.height])

  const smallVideosContainerPosition: string = React.useMemo(() => {
    if (fullScreen || isLandscape) {
      return null
    }
    return screenSize.height === videoSize.height
      ? `calc(100% - ${heightSmallScreensContainer} - ${FOOTER_HEIGHT}px)`
      : `${smallVideosPosition}px`
  }, [
    fullScreen,
    heightSmallScreensContainer,
    isLandscape,
    screenSize.height,
    smallVideosPosition,
    videoSize.height
  ])

  const smallScreensStyling: React.CSSProperties = React.useMemo(() => {
    return {
      position: isOnlyScreensView && !isTabletView ? "initial" : "absolute",
      width: "100%",
      height:
        room.showInteractivityRequiredVisual === true
          ? "0px"
          : heightSmallScreensContainer,
      marginTop:
        watchPartyHostId === "modNotPresentVideo"
          ? "0px"
          : watchPartyHostId !== "modNotPresentVideo" &&
            (userIdsSmallWindows.length !== 0 || isWatchParty) &&
            "10px",
      zIndex: 11,
      top: smallVideosContainerPosition,
      visibility: fullScreen || isLandscape ? "hidden" : "unset",
      bottom: fullScreen ? "60px" : "15px",
      transition: screenSize.height === videoSize.height ? "all 0.3s" : "unset"
    }
  }, [
    room.showInteractivityRequiredVisual,
    heightSmallScreensContainer,
    userIdsSmallWindows.length,
    isWatchParty,
    isOnlyScreensView,
    smallVideosContainerPosition,
    fullScreen,
    isLandscape,
    isTabletView,
    watchPartyHostId,
    screenSize.height,
    videoSize.height
  ])

  const widthSmallScreen = React.useMemo<number>(() => {
    return findSideRatio(heightPrevSpeaker, 16, 9)
  }, [heightPrevSpeaker])

  const smallScreenStyling: React.CSSProperties = React.useMemo(() => {
    return {
      width: widthSmallScreen,
      height: heightPrevSpeaker,
      margin: "0 0.5rem",
      backgroundColor:
        room?.branding?.alertBackgroundColor !== ""
          ? room?.branding?.alertBackgroundColor
          : "black"
    }
  }, [
    heightPrevSpeaker,
    widthSmallScreen,
    room?.branding?.alertBackgroundColor
  ])

  React.useEffect(() => {
    if (isLandscape && fullScreen) {
      setFullScreen(false)
      setIsWasFullScreen(true)
    }
    if (isWasFullScreen && !isLandscape) {
      setFullScreen(true)
      setIsWasFullScreen(false)
    }
  }, [fullScreen, isLandscape, isWasFullScreen, setFullScreen])

  React.useEffect(() => {
    if (isLandscape && !isSafari) {
      document.getElementsByTagName("body")[0].style.background = "black"
    }
    if (!isLandscape && !isSafari) {
      document.getElementsByTagName("body")[0].style.background = "white"
    }
  }, [isLandscape, isSafari])

  /* const handleCurrentSize = React.useCallback( async () => {
        const rootElement = (await waitForElm("root")) as HTMLDivElement
        const videoContainerElement = (await waitForElm(
          "video-wrapper"
        )) as HTMLDivElement
        if (isWatchParty) {
          const controlPlayerElement = (await waitForElm(
            "id-controls"
          )) as HTMLDivElement
          controlPlayerElement.style.position = "absolute"
          controlPlayerElement.style.left = isLandscape ? "50%" : "unset"
        }
        setScreenSize({
          width: rootElement.clientWidth,
          height: rootElement.clientHeight
        })
        videoContainerElement.style.left = "50%"
        videoContainerElement.style.position = isLandscape ? "fixed" : "relative"
        videoContainerElement.style.transform = "translate(-50%)"
        setVideoSize({
          width: isLandscape ? findSideRatio(screenSize.height, 16, 9) : screenSize.width,
          height: defaultHeightMainScreen
        })
      }, [defaultHeightMainScreen, isLandscape, isWatchParty, screenSize.height, screenSize.width, setScreenSize])

      React.useEffect(() => {
        if (isChromeIOS) {
          let timer = setTimeout(() => {
            handleCurrentSize()
          }, 500)
          return () => clearTimeout(timer)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isLandscape]) */

  React.useEffect(() => {
    if (isNativeAppBrowser) {
      let timer = setTimeout(() => {
        setScreenSize({
          width: window.innerWidth,
          height: window.innerHeight
        })
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isLandscape, isNativeAppBrowser, setScreenSize])

  const hideTabs = React.useMemo(
    () => screenSize.height - 85 <= videoSize.height || fullScreen,
    [fullScreen, screenSize.height, videoSize.height]
  )

  const chatResizeHandles = React.useMemo(
    () => (!fullScreen ? ["n"] : ["w"]),
    [fullScreen]
  )
  const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)

  const makeVideoPositionCenter = isOnlyScreensView && !isFloatingDivActive

  const isTransparentRef = React.useRef(isTransparent);
  isTransparentRef.current = isTransparent;

  // Store the value inside the ref so that the viewport listener can access the latest value
	const isReplySelected = useRecoilValue(selectedReplyAtom)
	const isReplySelectedRef = React.useRef(!!isReplySelected);
  isReplySelectedRef.current = !!isReplySelected;

  const prevVideoHeightRef = React.useRef(0);

  React.useEffect(() => {
    let pendingUpdate = false;

    function viewportHandler(event) {
      if (pendingUpdate) return;
      pendingUpdate = true;
    
      requestAnimationFrame(() => {
        pendingUpdate = false;
        const mainWindowContainer = document.getElementById(MAIN_WINDOW_CONTAINER);
        
        // visual viewport's offset from the layout viewport origin.
        const viewport = event.target;
        // if offsetTop is not a 0, then keyboard is open
        const isKeyboardOpen = !!viewport.offsetTop
        setIsKeyboardActive(isKeyboardOpen)

        // Hide bottom placing in safe area
        if(isKeyboardOpen) {
          setEnableSafeArea(prev => ({...prev, enableBottomSafeArea: false}))
        } else {
          setEnableSafeArea(prev => ({...prev, enableBottomSafeArea: true}))
        }

        // Set main window draggable false when keyboard open and vice verse
        setIsDraggable(!isKeyboardOpen)

        // Get offset height
        const offsetTop = viewport.offsetTop

        // if(isKeyboardOpen && !isTransparentRef.current) {
        //   mainWindowContainer.style.transform = "transform 0.5s ease"
        // }

        // setting top padding to make main video visible 
        mainWindowContainer.style.transform = `translate(${0}px, ${offsetTop}px) scale(${1 / viewport.scale})`;
        mainWindowContainer.style.zIndex = "100000"
        const newHeight =  viewport.height - HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB - windowDimensions.safeAreaTop // - windowDimensions.safeAreaBottom

        if(isTransparentRef.current) {
          // for fullscreen video, set the video size to available space
          if(isKeyboardOpen) {
            setVideoSize(prev => ({ ...prev, height: newHeight }))
          } else {
            setVideoSize(prev => ({ ...prev, height: newHeight - windowDimensions.safeAreaBottom }))
          }

        } else {
          setVideoSize(prev => {

            // Set the video height before keyboard opens
            if(prevVideoHeightRef.current < prev.height) {
              prevVideoHeightRef.current = prev.height
            }

            // case 1 - when keyboard is closed - apply prev video height
            if(!isKeyboardOpen) {
              const height = prevVideoHeightRef.current;
              prevVideoHeightRef.current = 0
              return { ...prev, height: height }
            }

            // case 2 - if video height is less then viewport height - don't change video height
            if(prev.height < newHeight) {
              return prev
            }

            // case 3 - if video height is greater viewport height - set available Height
            return {
              ...prev,
              height:
                newHeight -
                // minus the chat input field height
                CHAT_INPUT_FIELD_HEIGHT -
                // minus selected reply height if present
                (isReplySelectedRef.current
                  ? SELECTED_REPLY_MESSAGE_HEIGHT
                  : 0)
            }
          })
        }

      });
    }
    
    window.visualViewport.addEventListener("resize", viewportHandler);
    window.visualViewport.addEventListener("scroll", viewportHandler);

    return () => {
      window.visualViewport.removeEventListener("resize", viewportHandler);
      window.visualViewport.removeEventListener("scroll", viewportHandler);
    }
  }, [])


  return (
    <Flex
      flexDirection={!isLandscape ? "column" : "row"}
      ref={mobileMeetingContainerRef}
      w="100%"
      h="100%"
      overflow="hidden"
      flexWrap="wrap"
      bgColor={roomStyles?.roomBackgroundColor ?? "gray.100"}
      bgImage={
        roomStyles?.roomBackgroundImage
          ? `url(${roomStyles.roomBackgroundImage})`
          : "none"
      }
      bgSize={"cover"}
    >
      {/*    <div
        style={{
          background: "green",
          width: "100px",
          height: "100px",
          position: "fixed",
          top: "0px",
          left: "40px",
          zIndex: 1000000000
        }}  
      >{`${screenWidth}-${screenHeight}-${windowWidth}-${ windowHeight}----${screenSize.height} && ++++++uuuu ${window.matchMedia("(orientation: landscape)").matches}`}</div> */}
      <Box
        pos={makeVideoPositionCenter ? "absolute" : "unset"}
        top={makeVideoPositionCenter && "45%"}
        left={makeVideoPositionCenter && "50%"}
        transform={makeVideoPositionCenter && "translate(-50%, -50%)"}
        id={MAIN_WINDOW_CONTAINER}
        // willChange= 'transform'
        // zIndex={2}
      >
        {!isLandscape && (
          <MobileHeader isLandscape={isLandscape} isSafari={isSafari} />
        )}
        <Flex minH="150px">
          {isModalBid && (
            <AuctionOverlayBid isMobile={true} isLandscape={isLandscape} />
          )}
          {isFinalModal && (
            <AuctionFinalOverlay isMobile={true} isLandscape={isLandscape} />
          )}

          <MobileMainMediaScreen
            videoConstraints={videoConstraints}
            isTransparent={isTransparent}
            onResizeVideo={onResizeVideo}
            videoSize={videoSize}
            isOnlyScreensView={isOnlyScreensView}
            localUserProperties={localUserProperties}
            isLandscape={isLandscape}
            isMWDraggable={isDraggable}
          />
        </Flex>
        {!isTabletView && (
          <Box style={smallScreensStyling} ref={smallScreensContainerRef}>
            {isPrevSpeakerStreams ? (
              <MobilePrevSpeakerStreams
                screenWidth={screenSize.width}
                isOnlyScreensView={isOnlyScreensView}
                otherUserIds={userIdsSmallWindows}
                smallScreenStyling={smallScreenStyling}
                localUserProperties={localUserProperties}
              />
            ) : null}
          </Box>
        )}
      {isKeyboardActive && <ShadowBar />}
      </Box>
    
      {isServicePanel && (
        <Flex position="absolute" bottom="0px">
          <Box
            width={chatSize.width}
            height={chatSize.height}
            handleSize={[10, 10]}
            resizeHandles={chatResizeHandles}
            axis={"y"}
            mb="0"
          >
            <MobileServicePanel
              isLandscape={isLandscape}
              localUserProperties={localUserProperties}
              isTransparent={isTransparent}
              chatSize={chatSize}
              isTablet={false}
              hideTabs={false} //hideTabs}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  )
}

export const MobileView = React.memo(_MobileView)
